import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BlockIcon from '@mui/icons-material/Block';
import { Alert, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { ApiError } from '../../../../lib/http/ApiError';
import { AppModal } from '../../../../components/ui/AppModal';
import { useNotification } from '../../../notifications/hooks/useNotification';
import { useUpdateUserStatus } from '../../hooks/useUpdateUserStatus';
import type { User } from '../../userTypes';

interface DeactivateUserDialogProps {
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Deactivation confirm dialog (HOMCO-1366). PATCH /users/:id/status { is_active:
 * false }. A 409 (last active admin) gets a dedicated inline message; the route's
 * other client errors (403 R7, 404, 400) fall back to the generic error text.
 */
export function DeactivateUserDialog({ user, onClose, onSuccess }: DeactivateUserDialogProps) {
  const { t } = useTranslation('users');
  const { updateUserStatus, error } = useUpdateUserStatus();
  const { notifySuccess } = useNotification();
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!user) return;
    setSubmitting(true);
    const updated = await updateUserStatus(user.id, false);
    setSubmitting(false);
    if (updated) {
      notifySuccess(t('actions.deactivate.success'));
      onSuccess();
      onClose();
    }
    // On failure the dialog stays open and renders the error below.
  };

  const isLastAdmin = ApiError.isApiError(error) && error.statusCode === 409;
  const errorMessage = error
    ? isLastAdmin
      ? t('actions.deactivate.lastAdmin')
      : t('actions.deactivate.error')
    : null;

  return (
    <AppModal
      open={user !== null}
      onClose={handleClose}
      disableClose={submitting}
      maxWidth="xs"
      titleId="deactivate-user-title"
      icon={<BlockIcon sx={th => ({ fontSize: th.iconSize.sm })} />}
      title={t('actions.deactivate.confirmTitle')}
      closeLabel={t('actions.deactivate.cancel')}
    >
      <DialogContent sx={{ pt: 2 }}>
        <Typography>
          {t('actions.deactivate.confirmMessage', { username: user?.username ?? '' })}
        </Typography>
        {errorMessage && (
          <Alert severity={isLastAdmin ? 'warning' : 'error'} sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          {t('actions.deactivate.cancel')}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
          {t('actions.deactivate.confirm')}
        </Button>
      </DialogActions>
    </AppModal>
  );
}
